import React, { useEffect, useState } from 'react';
import { UploadCloud, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { businessPublicService, businessAdminService } from '../../../services/businessApi';
import { useAuth } from '../../../context/AuthContext';
import '../../../styles/businessTheme.css';
import './TrackerImport.css';

const ACTION_LABEL = { create: 'Create', update: 'Update', error: 'Error' };

// Two-step validate/preview -> confirm flow (Task 7), matching the backend contract exactly:
// nothing is committed until the coordinator reviews the per-row summary and confirms.
export default function TrackerImport() {
  const { isBusinessLead } = useAuth();
  const [plants, setPlants] = useState([]);
  const [plantId, setPlantId] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmResult, setConfirmResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    businessPublicService.listPlants().then(res => setPlants(res.data.plants));
  }, []);

  if (!isBusinessLead) return <div className="empty-state">Only business support coordinators can import the tracker.</div>;

  const handlePreview = async (e) => {
    e.preventDefault();
    if (!plantId || !file) { setError('Select a plant and a CSV file'); return; }
    setError('');
    setConfirmResult(null);
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('plantId', plantId);
      const res = await businessAdminService.importPreview(formData);
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview?.importToken) return;
    setBusy(true);
    setError('');
    try {
      const res = await businessAdminService.importConfirm(preview.importToken);
      setConfirmResult(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setConfirmResult(null);
    setError('');
  };

  return (
    <div className="tracker-import-page biz-theme biz-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Import Supplier-Plant Tracker</h1>
          <p className="page-subtitle">New suppliers are created; suppliers already mapped to this plant get their statuses updated — nothing is saved until you confirm</p>
        </div>
      </div>

      {error && <div className="form-error" style={{ marginBottom: 14 }}>{error}</div>}

      {!preview && (
        <div className="card">
          <form onSubmit={handlePreview} className="tracker-import-form">
            <div className="form-row">
              <div className="form-group">
                <label>Plant</label>
                <select value={plantId} onChange={e => setPlantId(e.target.value)} required>
                  <option value="">Select plant...</option>
                  {plants.map(p => <option key={p._id} value={p._id}>{p.name} ({p.code})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Tracker CSV file</label>
                <input type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0] || null)} required />
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              <UploadCloud size={14} /> {busy ? 'Validating...' : 'Preview Import'}
            </button>
          </form>
        </div>
      )}

      {preview && !confirmResult && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="form-section-title">{preview.plant.name} ({preview.plant.code})</div>
            <div className="tracker-import-summary">
              <div><strong>{preview.summary.totalRows}</strong> rows</div>
              <div className="tracker-import-summary__ok"><CheckCircle size={14} /> {preview.summary.toCreate} to create</div>
              <div className="tracker-import-summary__update">{preview.summary.toUpdate} to update</div>
              <div className="tracker-import-summary__warn"><AlertTriangle size={14} /> {preview.summary.withWarnings} with warnings</div>
              <div className="tracker-import-summary__err"><XCircle size={14} /> {preview.summary.errored} errors</div>
            </div>
            {preview.summary.unrecognizedColumns.length > 0 && (
              <p className="tracker-import-unrecognized">
                Unrecognized columns (ignored): {preview.summary.unrecognizedColumns.join(', ')}
              </p>
            )}
            {preview.summary.duplicateStepColumns?.length > 0 && (
              <p className="tracker-import-unrecognized">
                Duplicate step columns (ignored, first occurrence used): {preview.summary.duplicateStepColumns.join(', ')}
              </p>
            )}
          </div>

          <div className="card tracker-import-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Row</th><th>Supplier</th><th>Code</th><th>Action</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map(r => (
                  <tr key={r.rowNumber}>
                    <td>{r.rowNumber}</td>
                    <td>{r.supplierName || '—'}</td>
                    <td>{r.vendorCode || '—'}</td>
                    <td><span className={`tracker-import-action tracker-import-action--${r.action}`}>{ACTION_LABEL[r.action]}</span></td>
                    <td>
                      {r.errors.map((e, i) => <div key={i} className="tracker-import-err">{e}</div>)}
                      {r.warnings.map((w, i) => <div key={i} className="tracker-import-warn">{w}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tracker-import-actions">
            <button className="btn btn-ghost" onClick={reset}>Cancel / Re-upload</button>
            <button className="btn btn-primary" onClick={handleConfirm} disabled={busy || (preview.summary.toCreate + preview.summary.toUpdate) === 0}>
              {busy ? 'Importing...' : `Confirm Import (${preview.summary.toCreate + preview.summary.toUpdate} rows)`}
            </button>
          </div>
        </>
      )}

      {confirmResult && (
        <div className="card">
          <div className="form-section-title">Import complete</div>
          <div className="tracker-import-summary">
            <div className="tracker-import-summary__ok"><CheckCircle size={14} /> {confirmResult.created} created</div>
            <div className="tracker-import-summary__update">{confirmResult.updated} updated</div>
            <div className="tracker-import-summary__err"><XCircle size={14} /> {confirmResult.failed} failed</div>
          </div>
          <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={reset}>Import another file</button>
        </div>
      )}
    </div>
  );
}
